import React, { useEffect, useState } from 'react'
import { DataContainer, ProfileWrapper } from './styled'
import { ButtonGroup, Input, BirthdayInput, PhoneInput, Button, AddressInput, Uploader, Avatar } from '@sabavision/dio'
import { useTranslation } from 'react-i18next'
import { updateUserData, updateAttribute } from '../../actions/user'
import { useDispatch, useSelector } from 'react-redux'
import { Istate, Iuser } from '../../reducers/types'
import { uploadFile } from '../../api/file'
import { updateUserRequest } from '../../api/profile'
import Validate from '../../validations'
import profileValidator from '../../validations/schema/profile'
import { useParams } from 'react-router-dom'
import { updateToastData } from '../../actions/toast'
import { checkFileFormat } from '../../tools/files'

interface Props {
  closeSection: () => void
}

function Profile(props: Props) {
  const { t } = useTranslation('translation', { keyPrefix: 'sidebar' })
  const dispatch = useDispatch()
  const urlParams = useParams()

  const user = useSelector((state: Istate) => state.user)

  const [data, setData] = useState<Iuser>(user)
  const [bodyRequest, setBodyRequest] = useState<any>({})

  const updateData = (key: string, value: string) => {
    setData({ ...data, [key]: value })
    setBodyRequest({ ...bodyRequest, [key]: value })
  }

  const [error, setErrors] = useState<Record<string, string | null>>({})

  useEffect(() => {
    setData(user)
  }, [user])

  const validationData = () => {
    let isValid = true
    const constrains = profileValidator(data.entity_type === 'legal_person' ? 'legal_person' : 'real_person')
    let err = {}
    Object.keys(bodyRequest).forEach((field) => {
      const errorMessage = Validate(bodyRequest, field, constrains)
      err = { ...err, [field]: errorMessage }
      if (errorMessage) {
        isValid = false
      }
    })
    setErrors({ ...err })
    return isValid
  }

  const handleRequest = () => {
    const isValid = validationData()
    if (!isValid) return

    let param = {
      unique_id: user.unique_id,
      entity_type:
        data.entity_type === 'legal_person' || data.entity_type === 'real_person' ? data.entity_type : 'real_person'
    }
    if (data.entity_type === 'legal_person') {
      param = {
        ...param,
        ...bodyRequest,
        national_id: bodyRequest.company_national_id,
        first_name: '',
        last_name: '',
        birth_date: ''
      }
    } else {
      param = {
        ...param,
        ...bodyRequest,
        company_name: '',
        company_registration_number: '',
        telephone: ''
      }
    }
    updateUserRequest(
      urlParams.userId,
      param,
      (res: any) => {
        dispatch(updateAttribute(res))
        dispatch(
          updateToastData({
            message: t('update_user.success'),
            type: 'success'
          })
        )
        props.closeSection()
      },
      (error: any) => {
        dispatch(
          updateToastData({
            message: error.response?.data?.message,
            type: 'error'
          })
        )
      }
    )
  }

  const handleUploadFile = (e: any, type: 'avatar' | 'national_id_card') => {
    checkFileFormat(e.files[0], '.jpg,.jpeg,.png')
      .then((res: any) => {
        if (res) {
          const file = e.files[0]
          const name = e.files[0].name
          const data = new FormData()
          data.append('file', file)
          data.append('title', name)
          data.append('type', type)
          uploadFile(
            user.unique_id,
            data,
            (res: any) => {
              if (type === 'avatar')
                dispatch(updateUserData({ ...{ unique_id: user.unique_id, avatar: res.data.unique_id } }))
              if (type === 'national_id_card')
                dispatch(updateUserData({ ...{ unique_id: user.unique_id, national_id_card: res.data.unique_id } }))
              dispatch(
                updateToastData({
                  message: 'فایل با موفقیت آپلود شد',
                  type: 'success'
                })
              )
            },
            () => {
              dispatch(
                updateToastData({
                  message: 'فایل بارگذاری نشد، بعدا تلاش کنید',
                  type: 'error'
                })
              )
            }
          )
        } else {
          dispatch(
            updateToastData({
              message: 'به دلیل فرمت نامناسب بارگذاری نشد',
              type: 'error'
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          updateToastData({
            message: 'فایل دارای خطا می باشد',
            type: 'error'
          })
        )
      })
  }

  const handleDeleteFile = (type: 'avatar' | 'national_id_card') => {
    dispatch(updateUserData({ ...{ unique_id: user.unique_id, [type]: '' } }))
  }

  const imageUploadedArrange = (data: any) => {
    if (data) {
      let temp = Array.isArray(data) ? [...data] : [{ ...data }]
      temp = temp.map((item: any) => {
        return { id: item.unique_id, imageURL: item.url, disabled: user.verification_status === 'verified' }
      })
      return temp
    } else {
      return []
    }
  }

  return (
    <DataContainer dir="ltr">
      <div className="direction-corrector" dir="rtl">
        <div className="profile-buttonGroup">
          <ButtonGroup
            active={user.entity_type === 'legal_person' ? 'legal_person' : 'real_person'}
            buttons={[
              {
                label: t('real'),
                value: 'real_person'
              },
              {
                label: t('legal'),
                value: 'legal_person'
              }
            ]}
            type="filter"
            disabled={user.verification_status === 'verified'}
            handleChange={(activeButton) => {
              updateData('entity_type', activeButton.value)
            }}
          />
        </div>
        <span className="title">
          <span className="iconsax-profile-circle" />
          {t('identify_data')}
        </span>
        <Avatar
          handleAvatarFileInputChange={(e) => handleUploadFile(e, 'avatar')}
          handleClickDeleteAvatar={() => handleDeleteFile('avatar')}
          limitSize={2}
          avatarURL={user?.avatar?.url}
        />
        <ProfileWrapper style={{ marginTop: '10px' }}>
          <div className="max-container">
            {data.entity_type !== 'legal_person' ? (
              <div className="form">
                <Input
                  handleChange={(value, name) => updateData(name, value)}
                  value={data.national_id}
                  isRequired
                  disabled={user.verification_status === 'verified'}
                  label={t('national_code')}
                  name={'national_id'}
                  error={error.national_id && t(error.national_id)}
                  type="profile"
                />
                <Input
                  handleChange={(value, name) => updateData(name, value)}
                  value={data.first_name}
                  isRequired
                  disabled={user.verification_status === 'verified'}
                  label={t('name')}
                  name={'first_name'}
                  error={error.first_name && t(error.first_name)}
                  type="profile-allow-space"
                />
                <Input
                  handleChange={(value, name) => updateData(name, value)}
                  value={data.last_name}
                  isRequired
                  disabled={user.verification_status === 'verified'}
                  label={t('family')}
                  name={'last_name'}
                  error={error.last_name && t(error.last_name)}
                  type="profile-allow-space"
                />
                <BirthdayInput
                  value={data.birth_date}
                  handleChange={(value) => updateData('birth_date', value)}
                  label={t('birthdate')}
                  isRequired
                  disabled={user.verification_status === 'verified'}
                />
              </div>
            ) : (
              <div className="form">
                <Input
                  handleChange={(value, name) => updateData(name, value)}
                  value={data.company_national_id}
                  isRequired
                  disabled={user.verification_status === 'verified'}
                  label={t('company_national_id')}
                  name={'company_national_id'}
                  error={error.company_national_id && t(error.company_national_id)}
                  type="profile"
                />
                <Input
                  handleChange={(value, name) => updateData(name, value)}
                  value={data.company_name}
                  isRequired
                  disabled={user.verification_status === 'verified'}
                  label={t('company_name')}
                  name={'company_name'}
                  error={error.company_name && t(error.company_name)}
                  type="profile-allow-space"
                />

                <Input
                  handleChange={(value) => updateData('company_registration_number', value)}
                  value={data.company_registration_number}
                  disabled={user.verification_status === 'verified'}
                  label={t('registration_number')}
                  name={'registration_number'}
                  error={error.company_registration_number && t(error.company_registration_number)}
                  type="profile"
                />

                <PhoneInput
                  phone={{
                    label: t('phone_number'),
                    isRequired: true
                  }}
                  code={{
                    label: t('company_city_code'),
                    isRequired: false
                  }}
                  handleChange={(value) => {
                    updateData('telephone', String(value))
                  }}
                  value={data.telephone}
                  disabled={user.verification_status === 'verified'}
                  // error={error.registration_number && t(error.registration_number)}
                  error={null}
                />
              </div>
            )}
            {data.entity_type === 'real_person' && (
              <div className="uploader">
                <span className="uploader-title">
                  {t('national_card_pic')}
                  <span className="required">*</span>
                </span>
                <Uploader
                  handleAdd={(e) => handleUploadFile(e, 'national_id_card')}
                  handleDelete={() => handleDeleteFile('national_id_card')}
                  message={t('tax_pic_condition')}
                  uploadPercentage={100}
                  imageUploaded={imageUploadedArrange(data.national_id_card)}
                  disabled={user.verification_status === 'verified'}
                />
              </div>
            )}
          </div>
        </ProfileWrapper>

        <span className="divider">
          <span>
            <span className="iconsax-personalcard"></span>
            <span>{t('postal_data')}</span>
          </span>
        </span>
        <ProfileWrapper>
          <div className="max-container">
            <Input
              handleChange={(value) => updateData('postal_code', value)}
              value={data.postal_code}
              isRequired
              disabled={user.verification_status === 'verified'}
              label={t('postal_code')}
              type="profile"
              error={error.postal_code && t(error.postal_code)}
              name="postal_code"
            />

            <AddressInput
              handleChange={(value) => updateData('address', value)}
              disabled={user.verification_status === 'verified'}
              value={data.address}
              label={t('address')}
              error={error.address && t(error.address)}
              isRequired
            />
            <Button
              data-test="button-profile"
              label={t('confirm')}
              fullWidth
              radius="10px"
              fontSize="12px"
              handleClick={handleRequest}
              disabled={user.verification_status === 'verified'}
            />
          </div>
        </ProfileWrapper>
      </div>
    </DataContainer>
  )
}

export default Profile
